/* Percentage display helper.

   The backend stores/accepts percentages as fractions ("0.number", e.g. 0.15
   for 15%). We want users to type/read whole numbers (15) without changing the
   backend or the existing validation regexes.

   shiftDecimal moves a number string's decimal point using pure string ops —
   no float math — so exact digits are preserved on the round trip. That means
   a value the user types as "12.34" becomes exactly "0.1234" (not
   0.12340000000001), and the fraction-based regexConditions in constants.jsx
   keep validating identically. */
import { useState } from 'react';

export function shiftDecimal(value, places) {
  if (value === '' || value === null || value === undefined) return '';
  let s = String(value).trim();
  let sign = '';
  if (s[0] === '+' || s[0] === '-') { sign = s[0] === '-' ? '-' : ''; s = s.slice(1); }
  // Not a plain number — pass it through unchanged so validation can reject it.
  if (s === '' || s === '.' || !/^\d*\.?\d*$/.test(s)) return String(value);
  const dot = s.indexOf('.');
  const digits = dot === -1 ? s : s.slice(0, dot) + s.slice(dot + 1);
  const intLen = dot === -1 ? s.length : dot;
  let point = intLen + places;
  let d = digits;
  if (point <= 0) { d = '0'.repeat(1 - point) + d; point = 1; }
  else if (point >= d.length) { d = d + '0'.repeat(point - d.length); }
  const intPart = d.slice(0, point).replace(/^0+(?=\d)/, '');
  const fracPart = d.slice(point).replace(/0+$/, '');
  const out = fracPart ? intPart + '.' + fracPart : intPart;
  return sign + out;
}

// fraction (0.15) -> percent (15) for display; percent (15) -> fraction (0.15) for storage.
export const fractionToPercent = (v) => shiftDecimal(v, 2);
export const percentToFraction = (v) => shiftDecimal(v, -2);

const PERCENT_DRAFT = /^\d*\.?\d*$/;

/* A controlled input that shows whole-number percentages to the user while
   reading/writing the fraction form the backend expects. It speaks fractions
   to its callbacks: onValueChange/onValueBlur receive a fraction string, so the
   existing change/blur handlers and validation regexes are used unchanged.

   While focused it shows the raw typed draft (so partial input like "12." or
   trailing zeros aren't clobbered by the round trip); when blurred it shows the
   canonical percent derived from the stored fraction. */
export function PercentInput({ value, onValueChange, onValueBlur, className, id, disabled, placeholder }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const display = focused ? draft : fractionToPercent(value);

  const handleChange = (e) => {
    const next = e.target.value;
    if (!PERCENT_DRAFT.test(next)) return; // ignore non-numeric keystrokes
    setDraft(next);
    onValueChange && onValueChange(percentToFraction(next));
  };

  return (
    <input
      id={id}
      className={className}
      type="text"
      inputMode="decimal"
      value={display}
      disabled={disabled}
      placeholder={placeholder}
      onFocus={() => { setDraft(fractionToPercent(value)); setFocused(true); }}
      onChange={handleChange}
      onBlur={(e) => { setFocused(false); onValueBlur && onValueBlur(percentToFraction(e.target.value)); }}
    />
  );
}
